import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { websiteBannerControllerPreviewBanner } from '@/generated/korza-api-client';
import type {
  CreateWebsiteBannerDto,
  PreviewWebsiteBannerDto,
} from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { useDebounce } from '@/hooks/use-debounce';
import type { SetPreviewFormSnapshotRef } from './use-website-banner-form';

const PREVIEW_DEBOUNCE_MS = 400;
const PREVIEW_STALE_TIME_MS = 10_000;

export interface UseWebsiteBannerPreviewParams {
  form: CreateWebsiteBannerDto;
  bannerId: string | null;
  hasBannerData: boolean;
  setPreviewFormSnapshotRef: SetPreviewFormSnapshotRef;
}

export function useWebsiteBannerPreview({
  form,
  bannerId,
  hasBannerData,
  setPreviewFormSnapshotRef,
}: UseWebsiteBannerPreviewParams) {
  const tenant = useTenant();
  const [previewFormSnapshot, setPreviewFormSnapshot] =
    useState<CreateWebsiteBannerDto>(form);

  useEffect(() => {
    setPreviewFormSnapshotRef.current = setPreviewFormSnapshot;
    return () => {
      setPreviewFormSnapshotRef.current = null;
    };
  }, [setPreviewFormSnapshot, setPreviewFormSnapshotRef]);

  const debouncedSetSnapshot = useDebounce((value: CreateWebsiteBannerDto) => {
    setPreviewFormSnapshot(value);
  }, PREVIEW_DEBOUNCE_MS);

  useEffect(() => {
    debouncedSetSnapshot(form);
  }, [form, debouncedSetSnapshot]);

  const previewDto: PreviewWebsiteBannerDto = {
    tenantId: tenant.id,
    content: previewFormSnapshot.content,
    theme: previewFormSnapshot.theme,
    customStyles: previewFormSnapshot.customStyles,
    position: previewFormSnapshot.position ?? null,
  };

  const previewQuery = useQuery({
    queryKey: ['website-banner-preview', tenant.id, JSON.stringify(previewDto)],
    queryFn: async (): Promise<string> => {
      const res = await websiteBannerControllerPreviewBanner({
        body: previewDto,
      });
      if (res.data == null) throw new Error('Preview unavailable');
      return typeof res.data === 'string' ? res.data : String(res.data);
    },
    enabled: Boolean(tenant.id) && (bannerId == null ? true : hasBannerData),
    staleTime: PREVIEW_STALE_TIME_MS,
  });

  const previewHtml =
    previewQuery.data != null && typeof previewQuery.data === 'string'
      ? previewQuery.data
      : null;

  return {
    previewFormSnapshot,
    setPreviewFormSnapshot,
    setPreviewFormSnapshotRef,
    previewQuery,
    previewHtml,
  };
}
